/**
 * Locale + formatting - TypeScript port of
 * frontend/public/js/locale-config.js. Same country map, same Intl-based
 * helpers, exposed as plain functions (no globals).
 */

type CountryCode =
  | 'US' | 'GB' | 'CA' | 'AU' | 'NZ' | 'IN' | 'ZA' | 'DE' | 'FR' | 'IT' | 'ES'
  | 'NL' | 'BE' | 'AT' | 'IE' | 'PT' | 'CH' | 'SE' | 'NO' | 'DK' | 'FI' | 'JP'
  | 'CN' | 'KR' | 'SG' | 'HK' | 'MY' | 'AE' | 'SA' | 'EG' | 'NG' | 'KE' | 'BR'
  | 'MX' | 'AR' | 'CL' | 'CO' | 'IL' | 'TR' | 'PL' | 'CZ' | 'RO' | 'HU' | 'PH'
  | 'TH' | 'ID' | 'VN' | 'PK' | 'BD' | 'LK' | 'GH' | 'TZ'

type LocaleEntry = {
  locale: string
  currency: string
  fiscalStartMonth: number
  tzDefault: string
}

const COUNTRY_LOCALE_MAP: Record<CountryCode, LocaleEntry> = {
  US: { locale: 'en-US', currency: 'USD', fiscalStartMonth: 1, tzDefault: 'America/New_York' },
  GB: { locale: 'en-GB', currency: 'GBP', fiscalStartMonth: 4, tzDefault: 'Europe/London' },
  CA: { locale: 'en-CA', currency: 'CAD', fiscalStartMonth: 1, tzDefault: 'America/Toronto' },
  AU: { locale: 'en-AU', currency: 'AUD', fiscalStartMonth: 7, tzDefault: 'Australia/Sydney' },
  NZ: { locale: 'en-NZ', currency: 'NZD', fiscalStartMonth: 4, tzDefault: 'Pacific/Auckland' },
  IN: { locale: 'en-IN', currency: 'INR', fiscalStartMonth: 4, tzDefault: 'Asia/Kolkata' },
  ZA: { locale: 'en-ZA', currency: 'ZAR', fiscalStartMonth: 3, tzDefault: 'Africa/Johannesburg' },
  DE: { locale: 'de-DE', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Berlin' },
  FR: { locale: 'fr-FR', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Paris' },
  IT: { locale: 'it-IT', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Rome' },
  ES: { locale: 'es-ES', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Madrid' },
  NL: { locale: 'nl-NL', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Amsterdam' },
  BE: { locale: 'nl-BE', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Brussels' },
  AT: { locale: 'de-AT', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Vienna' },
  IE: { locale: 'en-IE', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Dublin' },
  PT: { locale: 'pt-PT', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Lisbon' },
  CH: { locale: 'de-CH', currency: 'CHF', fiscalStartMonth: 1, tzDefault: 'Europe/Zurich' },
  SE: { locale: 'sv-SE', currency: 'SEK', fiscalStartMonth: 1, tzDefault: 'Europe/Stockholm' },
  NO: { locale: 'nb-NO', currency: 'NOK', fiscalStartMonth: 1, tzDefault: 'Europe/Oslo' },
  DK: { locale: 'da-DK', currency: 'DKK', fiscalStartMonth: 1, tzDefault: 'Europe/Copenhagen' },
  FI: { locale: 'fi-FI', currency: 'EUR', fiscalStartMonth: 1, tzDefault: 'Europe/Helsinki' },
  JP: { locale: 'ja-JP', currency: 'JPY', fiscalStartMonth: 4, tzDefault: 'Asia/Tokyo' },
  CN: { locale: 'zh-CN', currency: 'CNY', fiscalStartMonth: 1, tzDefault: 'Asia/Shanghai' },
  KR: { locale: 'ko-KR', currency: 'KRW', fiscalStartMonth: 1, tzDefault: 'Asia/Seoul' },
  SG: { locale: 'en-SG', currency: 'SGD', fiscalStartMonth: 1, tzDefault: 'Asia/Singapore' },
  HK: { locale: 'en-HK', currency: 'HKD', fiscalStartMonth: 4, tzDefault: 'Asia/Hong_Kong' },
  MY: { locale: 'ms-MY', currency: 'MYR', fiscalStartMonth: 1, tzDefault: 'Asia/Kuala_Lumpur' },
  AE: { locale: 'ar-AE', currency: 'AED', fiscalStartMonth: 1, tzDefault: 'Asia/Dubai' },
  SA: { locale: 'ar-SA', currency: 'SAR', fiscalStartMonth: 1, tzDefault: 'Asia/Riyadh' },
  EG: { locale: 'ar-EG', currency: 'EGP', fiscalStartMonth: 7, tzDefault: 'Africa/Cairo' },
  NG: { locale: 'en-NG', currency: 'NGN', fiscalStartMonth: 1, tzDefault: 'Africa/Lagos' },
  KE: { locale: 'en-KE', currency: 'KES', fiscalStartMonth: 7, tzDefault: 'Africa/Nairobi' },
  BR: { locale: 'pt-BR', currency: 'BRL', fiscalStartMonth: 1, tzDefault: 'America/Sao_Paulo' },
  MX: { locale: 'es-MX', currency: 'MXN', fiscalStartMonth: 1, tzDefault: 'America/Mexico_City' },
  AR: { locale: 'es-AR', currency: 'ARS', fiscalStartMonth: 1, tzDefault: 'America/Argentina/Buenos_Aires' },
  CL: { locale: 'es-CL', currency: 'CLP', fiscalStartMonth: 1, tzDefault: 'America/Santiago' },
  CO: { locale: 'es-CO', currency: 'COP', fiscalStartMonth: 1, tzDefault: 'America/Bogota' },
  IL: { locale: 'he-IL', currency: 'ILS', fiscalStartMonth: 1, tzDefault: 'Asia/Jerusalem' },
  TR: { locale: 'tr-TR', currency: 'TRY', fiscalStartMonth: 1, tzDefault: 'Europe/Istanbul' },
  PL: { locale: 'pl-PL', currency: 'PLN', fiscalStartMonth: 1, tzDefault: 'Europe/Warsaw' },
  CZ: { locale: 'cs-CZ', currency: 'CZK', fiscalStartMonth: 1, tzDefault: 'Europe/Prague' },
  RO: { locale: 'ro-RO', currency: 'RON', fiscalStartMonth: 1, tzDefault: 'Europe/Bucharest' },
  HU: { locale: 'hu-HU', currency: 'HUF', fiscalStartMonth: 1, tzDefault: 'Europe/Budapest' },
  PH: { locale: 'en-PH', currency: 'PHP', fiscalStartMonth: 1, tzDefault: 'Asia/Manila' },
  TH: { locale: 'th-TH', currency: 'THB', fiscalStartMonth: 10, tzDefault: 'Asia/Bangkok' },
  ID: { locale: 'id-ID', currency: 'IDR', fiscalStartMonth: 1, tzDefault: 'Asia/Jakarta' },
  VN: { locale: 'vi-VN', currency: 'VND', fiscalStartMonth: 1, tzDefault: 'Asia/Ho_Chi_Minh' },
  PK: { locale: 'en-PK', currency: 'PKR', fiscalStartMonth: 7, tzDefault: 'Asia/Karachi' },
  BD: { locale: 'bn-BD', currency: 'BDT', fiscalStartMonth: 7, tzDefault: 'Asia/Dhaka' },
  LK: { locale: 'si-LK', currency: 'LKR', fiscalStartMonth: 4, tzDefault: 'Asia/Colombo' },
  GH: { locale: 'en-GH', currency: 'GHS', fiscalStartMonth: 1, tzDefault: 'Africa/Accra' },
  TZ: { locale: 'sw-TZ', currency: 'TZS', fiscalStartMonth: 7, tzDefault: 'Africa/Dar_es_Salaam' },
}

const COUNTRY_NAMES: Record<CountryCode, string> = {
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia', NZ: 'New Zealand',
  IN: 'India', ZA: 'South Africa', DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain',
  NL: 'Netherlands', BE: 'Belgium', AT: 'Austria', IE: 'Ireland', PT: 'Portugal', CH: 'Switzerland',
  SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', JP: 'Japan', CN: 'China',
  KR: 'South Korea', SG: 'Singapore', HK: 'Hong Kong', MY: 'Malaysia', AE: 'UAE', SA: 'Saudi Arabia',
  EG: 'Egypt', NG: 'Nigeria', KE: 'Kenya', BR: 'Brazil', MX: 'Mexico', AR: 'Argentina',
  CL: 'Chile', CO: 'Colombia', IL: 'Israel', TR: 'Turkey', PL: 'Poland', CZ: 'Czech Republic',
  RO: 'Romania', HU: 'Hungary', PH: 'Philippines', TH: 'Thailand', ID: 'Indonesia', VN: 'Vietnam',
  PK: 'Pakistan', BD: 'Bangladesh', LK: 'Sri Lanka', GH: 'Ghana', TZ: 'Tanzania',
}

export type UserLocale = LocaleEntry & { country: CountryCode; dateFormat: string }

/**
 * Source of truth precedence:
 *   1. Tenant settings (set by <TenantLocaleSync /> after `useTenantSettings`
 *      resolves) - this is the saved Regional Settings on the Company page.
 *   2. localStorage mirrors (`userCountry`, `userTimezone`, `userFiscalStartMonth`) -
 *      survives page reloads before the React tree mounts and the tenant query
 *      finishes; also covers logged-out flows.
 *   3. Hardcoded US fallback - last resort.
 */
type TenantLocalePatch = {
  country?: string | null
  locale?: string | null
  currency?: string | null
  date_format?: string | null
  fiscal_year_start?: number | null
  timezone?: string | null
}

let cached: UserLocale | null = null

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function isCountryCode(v: unknown): v is CountryCode {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(COUNTRY_LOCALE_MAP, v)
}

/**
 * Reverse lookup: given a currency code, return the country code that uses
 * it - but only if the mapping is unambiguous (e.g. GBP→GB, USD→US, JPY→JP).
 * Shared currencies like EUR (DE/FR/IT/…) return null so callers don't pick
 * the wrong country. Used to back-fill a country field on records hydrated
 * with a saved currency but no saved country.
 */
export function countryForCurrency(currency: string | null | undefined): string | null {
  if (!currency) return null
  const upper = currency.toUpperCase()
  const codes = Object.keys(COUNTRY_LOCALE_MAP) as CountryCode[]
  const matches = codes.filter((c) => COUNTRY_LOCALE_MAP[c].currency === upper)
  return matches.length === 1 ? matches[0] : null
}

/**
 * Aliases the AI extractor or older saved records may use for a country.
 * Maps common full names, ISO-3 codes, and informal short forms to the
 * ISO-2 codes the CountryCombobox expects.
 */
const COUNTRY_ALIASES: Record<string, CountryCode> = {
  USA: 'US', 'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', AMERICA: 'US',
  UK: 'GB', GBR: 'GB', 'UNITED KINGDOM': 'GB', BRITAIN: 'GB', 'GREAT BRITAIN': 'GB', ENGLAND: 'GB', SCOTLAND: 'GB', WALES: 'GB',
  CAN: 'CA', CANADA: 'CA',
  AUS: 'AU', AUSTRALIA: 'AU',
  NZL: 'NZ', 'NEW ZEALAND': 'NZ',
  IND: 'IN', INDIA: 'IN',
  ZAF: 'ZA', 'SOUTH AFRICA': 'ZA',
  DEU: 'DE', GER: 'DE', GERMANY: 'DE', DEUTSCHLAND: 'DE',
  FRA: 'FR', FRANCE: 'FR',
  ITA: 'IT', ITALY: 'IT', ITALIA: 'IT',
  ESP: 'ES', SPAIN: 'ES', ESPAÑA: 'ES',
  NLD: 'NL', NETHERLANDS: 'NL', HOLLAND: 'NL',
  BEL: 'BE', BELGIUM: 'BE',
  AUT: 'AT', AUSTRIA: 'AT',
  IRL: 'IE', IRELAND: 'IE', EIRE: 'IE',
  PRT: 'PT', PORTUGAL: 'PT',
  CHE: 'CH', SWITZERLAND: 'CH',
  SWE: 'SE', SWEDEN: 'SE',
  NOR: 'NO', NORWAY: 'NO',
  DNK: 'DK', DENMARK: 'DK',
  FIN: 'FI', FINLAND: 'FI',
  JPN: 'JP', JAPAN: 'JP',
  CHN: 'CN', CHINA: 'CN',
  KOR: 'KR', 'SOUTH KOREA': 'KR', 'REPUBLIC OF KOREA': 'KR', KOREA: 'KR',
  SGP: 'SG', SINGAPORE: 'SG',
  HKG: 'HK', 'HONG KONG': 'HK',
  MYS: 'MY', MALAYSIA: 'MY',
  ARE: 'AE', UAE: 'AE', 'UNITED ARAB EMIRATES': 'AE',
  SAU: 'SA', 'SAUDI ARABIA': 'SA', KSA: 'SA',
  EGY: 'EG', EGYPT: 'EG',
  NGA: 'NG', NIGERIA: 'NG',
  KEN: 'KE', KENYA: 'KE',
  BRA: 'BR', BRAZIL: 'BR', BRASIL: 'BR',
  MEX: 'MX', MEXICO: 'MX',
  ARG: 'AR', ARGENTINA: 'AR',
  CHL: 'CL', CHILE: 'CL',
  COL: 'CO', COLOMBIA: 'CO',
  ISR: 'IL', ISRAEL: 'IL',
  TUR: 'TR', TURKEY: 'TR', TÜRKIYE: 'TR',
  POL: 'PL', POLAND: 'PL',
  CZE: 'CZ', CZECHIA: 'CZ', 'CZECH REPUBLIC': 'CZ',
  ROU: 'RO', ROMANIA: 'RO',
  HUN: 'HU', HUNGARY: 'HU',
  PHL: 'PH', PHILIPPINES: 'PH',
  THA: 'TH', THAILAND: 'TH',
  IDN: 'ID', INDONESIA: 'ID',
  VNM: 'VN', VIETNAM: 'VN',
  PAK: 'PK', PAKISTAN: 'PK',
  BGD: 'BD', BANGLADESH: 'BD',
  LKA: 'LK', 'SRI LANKA': 'LK',
  GHA: 'GH', GHANA: 'GH',
  TZA: 'TZ', TANZANIA: 'TZ',
}

/**
 * Coerce any country input the extractor or a legacy DB record may produce
 * into the ISO-2 code that CountryCombobox / regional helpers expect. Returns
 * null when no plausible match exists so callers can fall back to currency-
 * derived defaults.
 */
export function normalizeCountryCode(input: string | null | undefined): string | null {
  if (!input) return null
  const trimmed = String(input).trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (isCountryCode(upper)) return upper
  if (COUNTRY_ALIASES[upper]) return COUNTRY_ALIASES[upper]
  // Last-resort: scan COUNTRY_NAMES for a case-insensitive label match.
  for (const [code, name] of Object.entries(COUNTRY_NAMES)) {
    if (name.toUpperCase() === upper) return code
  }
  return null
}

export function defaultDateFormatForCountry(country: string): string {
  // ISO countries we care about that don't use US-style; default everyone else
  // to the same format as the locale string would render.
  if (country === 'US') return 'MM/DD/YYYY'
  if (country === 'CA') return 'YYYY-MM-DD'
  if (country === 'JP' || country === 'CN' || country === 'KR') return 'YYYY/MM/DD'
  return 'DD/MM/YYYY'
}

function buildFromCountry(country: CountryCode, overrides: Partial<UserLocale> = {}): UserLocale {
  const defaults = COUNTRY_LOCALE_MAP[country] ?? COUNTRY_LOCALE_MAP.US
  return {
    country,
    locale: overrides.locale ?? defaults.locale,
    currency: overrides.currency ?? defaults.currency,
    fiscalStartMonth: overrides.fiscalStartMonth ?? defaults.fiscalStartMonth,
    tzDefault: overrides.tzDefault ?? defaults.tzDefault,
    dateFormat: overrides.dateFormat ?? defaultDateFormatForCountry(country),
  }
}

export function getUserLocale(): UserLocale {
  if (cached) return cached
  const country = (readLocalStorage('userCountry') as CountryCode) || 'US'
  const tz = readLocalStorage('userTimezone')
  const fiscalOverride = readLocalStorage('userFiscalStartMonth')
  cached = buildFromCountry(country, {
    fiscalStartMonth: fiscalOverride ? parseInt(fiscalOverride, 10) : undefined,
    tzDefault: tz || undefined,
  })
  return cached
}

/**
 * Update the module cache from tenant Regional Settings. Call this from
 * <TenantLocaleSync /> whenever the `tenant-settings` query resolves so every
 * formatter (`formatCurrency`, `formatDate`, …) and every form-default lookup
 * sees the saved values. Also mirrors to localStorage so a hard reload keeps
 * the same locale before React mounts.
 */
export function setTenantLocale(t: TenantLocalePatch | null | undefined): void {
  if (!t) return
  const country: CountryCode = isCountryCode(t.country) ? t.country : 'US'
  const defaults = COUNTRY_LOCALE_MAP[country] ?? COUNTRY_LOCALE_MAP.US
  cached = {
    country,
    locale: t.locale || defaults.locale,
    currency: (t.currency || defaults.currency).toUpperCase(),
    fiscalStartMonth: typeof t.fiscal_year_start === 'number'
      ? t.fiscal_year_start
      : defaults.fiscalStartMonth,
    tzDefault: t.timezone || defaults.tzDefault,
    dateFormat: t.date_format || defaultDateFormatForCountry(country),
  }
  try {
    localStorage.setItem('userCountry', country)
    if (t.timezone) localStorage.setItem('userTimezone', t.timezone)
    if (typeof t.fiscal_year_start === 'number') {
      localStorage.setItem('userFiscalStartMonth', String(t.fiscal_year_start))
    }
  } catch {
    // localStorage unavailable; cache still works for the session.
  }
}

export function resetLocaleCache(): void {
  cached = null
}

export function formatCurrency(amount: number): string {
  const cfg = getUserLocale()
  return new Intl.NumberFormat(cfg.locale, {
    style: 'currency',
    currency: cfg.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(amount))
}

export function formatCurrencyPrecise(amount: number): string {
  const cfg = getUserLocale()
  return new Intl.NumberFormat(cfg.locale, {
    style: 'currency',
    currency: cfg.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function formatNumber(n: number, decimals = 0): string {
  const cfg = getUserLocale()
  return new Intl.NumberFormat(cfg.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n)
}

function applyDateFormatPattern(d: Date, pattern: string, locale: string, tz?: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    timeZone: tz,
  }).formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  const yyyy = get('year')
  const mm = get('month')
  const dd = get('day')
  switch ((pattern || '').toUpperCase()) {
    case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`
    case 'YYYY/MM/DD': return `${yyyy}/${mm}/${dd}`
    case 'DD-MM-YYYY': return `${dd}-${mm}-${yyyy}`
    case 'DD.MM.YYYY': return `${dd}.${mm}.${yyyy}`
    case 'MM/DD/YYYY':
    default:           return `${mm}/${dd}/${yyyy}`
  }
}

export function formatDate(value: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
  const cfg = getUserLocale()
  const d = value instanceof Date ? value : new Date(value)
  if (options) {
    return d.toLocaleDateString(cfg.locale, options)
  }
  // No explicit options -> honor the tenant's saved date_format pattern.
  return applyDateFormatPattern(d, cfg.dateFormat, cfg.locale)
}

export function formatDateTime(value: Date | string | number): string {
  const cfg = getUserLocale()
  const d = value instanceof Date ? value : new Date(value)
  return d.toLocaleString(cfg.locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: cfg.tzDefault,
  })
}

export function formatMonthYear(value: Date | string | number): string {
  const cfg = getUserLocale()
  const d = value instanceof Date ? value : new Date(value)
  return d.toLocaleString(cfg.locale, { month: 'long', year: 'numeric' })
}

export function getCurrencySymbol(): string {
  const cfg = getUserLocale()
  const parts = new Intl.NumberFormat(cfg.locale, {
    style: 'currency',
    currency: cfg.currency,
  }).formatToParts(0)
  return parts.find((p) => p.type === 'currency')?.value ?? '$'
}

export { COUNTRY_LOCALE_MAP, COUNTRY_NAMES }
